import React, { useCallback, useEffect, useMemo, useState } from 'react';
import leadsWebApi from '../../services/leads-web';
import usersApi from '../../services/users';
import { referentiels as referentielsApi } from '../../services/referentiels';
import { texteTheme } from '../../utils/theme';
import {
  ActionsContact, Bloc, CarteLigne, Feuille, Fiche, Kv, LienOrdinateur, Liste, Pill, Puces, Recherche,
  SaisieCommentaire, Segments, Statut, depuis, messageErreur, normaliser, quand, useFicheOuverte, useToast,
} from './MobileUI';

// ═══════════════════════════════════════════════════════════════════════════
// Leads web — version téléphone (2026-10-24).
//
// Le cas d'usage : un vendeur reçoit un lead le soir ou le week-end (53 % des
// arrivées) et rappelle tout de suite. Il arrive sur SES leads (puce « Mes
// leads », par défaut pour un rôle `operateur`) ; « Non assignés » et « Tous »
// restent à un pouce. Fiche : Appeler / E-mail, message, véhicule, vendeur,
// commentaires ; une action principale, « Changer le statut ».
//
// Mêmes routes et mêmes règles que LeadsView.js :
//   - statuts du référentiel `statuts_leads_web`, filtrés par service et par
//     jeu (suivi interne ou non) de la concession du lead ;
//   - CRM360 : n° + compte rendu de 20 caractères minimum (contrôlé aussi par
//     le serveur), puis archivage ;
//   - autre statut archivant : `statut = archive` + commentaire, comme sur
//     ordinateur.
// Reste sur ordinateur : tableau de bord, funnel, suivi, configuration,
// import, export, actions de masse, suppression, transformation en demande,
// changement de concession, historique des modifications.
// ═══════════════════════════════════════════════════════════════════════════

const SERVICES = [
  { cle: 'VN', libelle: 'VN' },
  { cle: 'VO', libelle: 'VO' },
  { cle: 'ATELIER', libelle: 'Atelier' },
  { cle: 'PIECES', libelle: 'Pièces' },
  { cle: 'AUTRE', libelle: 'Autres' },
];
const CR_CRM360_MIN = 20;
const GRIS_HERITE = 'var(--text-muted)';

const nomLead = (l) => [l.firstname, l.name].filter(Boolean).join(' ').trim() || 'Sans nom';
const vehiculeLead = (l) => [l.brand, l.model].filter(Boolean).join(' ');
const nomVendeur = (v) => [v?.prenom, v?.nom].filter(Boolean).join(' ') || v?.username || '';

function groupeDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Plus ancien';
  const auj = new Date(); auj.setHours(0, 0, 0, 0);
  const jour = new Date(d); jour.setHours(0, 0, 0, 0);
  const ecart = Math.round((auj - jour) / 86400000);
  if (ecart <= 0) return 'Aujourd’hui';
  if (ecart === 1) return 'Hier';
  if (ecart < 7) return 'Cette semaine';
  if (ecart < 31) return 'Ce mois-ci';
  return 'Plus d’un mois';
}

export default function LeadsMobile({ user, onVersionOrdinateur, openLeadId, onLeadOpened }) {
  const estVendeur = user?.role === 'operateur';
  const [leads, setLeads] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [statuts, setStatuts] = useState([]);
  const [vendeurs, setVendeurs] = useState([]);
  const [sources, setSources] = useState([]);
  const [service, setService] = useState('VN');
  const [file, setFile] = useState(estVendeur ? 'miens' : 'tous');
  const [recherche, setRecherche] = useState('');
  const [toast, afficher] = useToast();

  const charger = useCallback(async () => {
    try {
      const r = await leadsWebApi.getAll({ actifs: true });
      setLeads((r.data.leads || []).filter((l) => l.statut !== 'archive'));
      setErreur('');
    } catch (e) {
      setErreur(messageErreur(e, 'Les leads n’ont pas pu être chargés.'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
    referentielsApi.getStatutsLeadsWeb({ actif: '1' }).then((r) => setStatuts(r.data.statuts_leads_web || [])).catch(() => {});
    referentielsApi.getSourcesMapping().then((r) => setSources(r.data.sources_mapping || [])).catch(() => {});
    usersApi.getAssignables().then((r) => setVendeurs(r.data.users || [])).catch(() => {});
  }, [charger]);

  // Pas de flux temps réel ici : la liste se recharge quand on revient sur
  // l'onglet du navigateur (cas typique : retour après un appel) et toutes les 2 min.
  useEffect(() => {
    const auRetour = () => { if (document.visibilityState === 'visible') charger(); };
    document.addEventListener('visibilitychange', auRetour);
    const minuteur = setInterval(auRetour, 120000);
    return () => { document.removeEventListener('visibilitychange', auRetour); clearInterval(minuteur); };
  }, [charger]);

  const libelleSource = useCallback((s) => {
    if (!s) return 'source inconnue';
    const m = sources.find((sm) => s.toLowerCase().includes(String(sm.pattern || '').toLowerCase()));
    return m ? m.label : s;
  }, [sources]);

  const optionsStatut = useCallback((lead) => {
    const svc = lead.service_concession || 'VN';
    const interne = !!lead.concession_suivi_interne;
    const opts = statuts.filter((s) => Array.isArray(s.services) && s.services.includes(svc)
      && (interne ? (s.suivi_interne || s.code === 'a_contacter') : !s.suivi_interne));
    if (lead.statut && !opts.find((o) => o.code === lead.statut)) {
      opts.unshift({ code: lead.statut, nom: lead.statut, couleur: GRIS_HERITE });
    }
    return opts;
  }, [statuts]);
  const statutDe = (lead) => statuts.find((s) => s.code === lead.statut) || { code: lead.statut, nom: lead.statut || '—', couleur: GRIS_HERITE };

  // Recherche : un n° exact (« 14903 » ou « #14903 ») passe outre service et file,
  // comme sur ordinateur ; sinon nom, téléphone, e-mail, marque, modèle.
  const q = recherche.trim();
  const numero = /^#?\d+$/.test(q) ? q.replace('#', '') : null;
  const qn = normaliser(q);
  const correspond = (l) => !qn || [nomLead(l), l.phone, l.email, l.brand, l.model, l.immatriculation]
    .some((v) => normaliser(v).includes(qn));


  const dansFile = useCallback((l) => (file === 'miens' ? String(l.vendeur_id) === String(user?.id)
    : file === 'libres' ? !l.vendeur_id : true), [file, user]);

  // Un vendeur arrive sur le service où il a des leads (VN n'est qu'un défaut).
  const [serviceChoisi, setServiceChoisi] = useState(false);
  useEffect(() => {
    if (serviceChoisi || chargement || !estVendeur) return;
    const miens = leads.filter((l) => String(l.vendeur_id) === String(user?.id));
    if (!miens.length) return;
    const parSvc = {};
    miens.forEach((l) => { const s = l.service_concession || 'VN'; parSvc[s] = (parSvc[s] || 0) + 1; });
    const meilleur = SERVICES.map((s) => s.cle).sort((a, b) => (parSvc[b] || 0) - (parSvc[a] || 0))[0];
    setService(meilleur);
    setServiceChoisi(true);
  }, [leads, chargement, estVendeur, serviceChoisi, user]);
  const choisirService = (s) => { setService(s); setServiceChoisi(true); };

  const duService = useMemo(() => leads.filter((l) => (l.service_concession || 'VN') === service), [leads, service]);
  const comptes = useMemo(() => ({
    miens: duService.filter((l) => String(l.vendeur_id) === String(user?.id)).length,
    libres: duService.filter((l) => !l.vendeur_id).length,
    tous: duService.length,
  }), [duService, user]);

  // Compteurs du sélecteur de service : ceux de la file choisie (« Mes leads »
  // montre où sont SES leads).
  const parService = useMemo(() => {
    const m = {};
    SERVICES.forEach((s) => { m[s.cle] = 0; });
    leads.forEach((l) => { const s = l.service_concession || 'VN'; if (m[s] != null && dansFile(l)) m[s] += 1; });
    return m;
  }, [leads, dansFile]);

  const visibles = useMemo(() => {
    let base;
    if (numero !== null) base = leads.filter((l) => String(l.id) === numero);
    else {
      base = duService.filter(dansFile).filter(correspond);
    }
    return [...base].sort((a, b) => new Date(b.date_reception) - new Date(a.date_reception));
  }, [leads, duService, dansFile, numero, qn]); // eslint-disable-line react-hooks/exhaustive-deps

  const cle = useCallback((l) => l.id, []);
  const fiche = useFicheOuverte(visibles, cle);

  // Lien direct `?page=leads&lead=<id>` (e-mail d'arrivée d'un lead, recherche
  // globale) : on cherche le lead par son n°, ce qui l'isole quel que soit le
  // service ou la file, puis on ouvre sa fiche.
  useEffect(() => {
    if (!openLeadId || chargement) return;
    setRecherche(String(openLeadId));
    if (onLeadOpened) onLeadOpened();
  }, [openLeadId, chargement]); // eslint-disable-line react-hooks/exhaustive-deps
  const [ouvrirNumero, setOuvrirNumero] = useState(null);
  useEffect(() => { if (openLeadId) setOuvrirNumero(String(openLeadId)); }, [openLeadId]);
  useEffect(() => {
    if (!ouvrirNumero || numero !== ouvrirNumero) return;
    if (visibles.length) fiche.ouvrir(0);
    setOuvrirNumero(null);
  }, [ouvrirNumero, numero, visibles]); // eslint-disable-line react-hooks/exhaustive-deps

  const majLocale = (id, champs) => setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...champs } : l)));
  const retirer = (id) => {
    // On passe au lead suivant de la file plutôt que de revenir à la liste.
    if (fiche.suivant) fiche.suivant(); else if (fiche.precedent) fiche.precedent(); else fiche.fermer();
    setLeads((prev) => prev.filter((l) => l.id !== id));
  };

  return (
    <div className="mb-page">
      <div className="mb-outils">
        <Recherche id="mb-leads-q" valeur={recherche} onChange={setRecherche} placeholder="N° de lead, nom, téléphone, modèle" />
        {numero === null && (
          <Segments label="Service" valeur={service} onChange={choisirService}
            options={SERVICES.map((s) => ({ ...s, compte: parService[s.cle] }))} />
        )}
      </div>
      {numero === null && (
        <Puces label="File" valeur={file} onChange={setFile} options={[
          { cle: 'miens', libelle: 'Mes leads', compte: comptes.miens },
          { cle: 'libres', libelle: 'Non assignés', compte: comptes.libres, alerte: true },
          { cle: 'tous', libelle: 'Tous', compte: comptes.tous },
        ]} />
      )}

      <Liste
        elements={visibles}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={numero !== null ? `Aucun lead actif n° ${numero}.` : file === 'miens' ? 'Aucun lead à votre nom dans ce service.' : 'Aucun lead dans cette file.'}
        groupe={(l) => groupeDate(l.date_reception)}
        onOuvrir={fiche.ouvrir}
        carte={(l) => {
          const st = statutDe(l);
          const heures = (Date.now() - new Date(l.date_reception).getTime()) / 3600000;
          return {
            couleur: st.couleur,
            contenu: (
              <>
                <CarteLigne titre={nomLead(l)} age={depuis(l.date_reception)} retard={heures > 12} />
                <span className="mb-carte-texte">{[vehiculeLead(l), libelleSource(l.source)].filter(Boolean).join(' · ')}</span>
                <span className="mb-carte-meta">
                  <Statut libelle={st.nom} couleur={st.couleur} />
                  {l.vendeur_id
                    ? <Pill ton="accent">{String(l.vendeur_id) === String(user?.id) ? 'À moi' : [l.vendeur_prenom, l.vendeur_nom].filter(Boolean).join(' ')}</Pill>
                    : <Pill ton="attente">Non assigné</Pill>}
                  {l.nb_commentaires > 0 && <Pill>{l.nb_commentaires} note{l.nb_commentaires > 1 ? 's' : ''}</Pill>}
                </span>
              </>
            ),
          };
        }}
      />
      <LienOrdinateur onClick={onVersionOrdinateur}>Tableau de bord, archives, export : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FicheLead
          key={fiche.ouverte.id}
          lead={fiche.ouverte}
          user={user}
          position={fiche.index}
          total={visibles.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          statut={statutDe(fiche.ouverte)}
          options={optionsStatut(fiche.ouverte)}
          vendeurs={vendeurs}
          libelleSource={libelleSource}
          onMaj={majLocale}
          onRetire={retirer}
          afficher={afficher}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {toast}
    </div>
  );
}

function FicheLead({ lead, user, position, total, onRetour, onPrecedent, onSuivant, statut, options, vendeurs,
  libelleSource, onMaj, onRetire, afficher, onVersionOrdinateur }) {
  const [commentaires, setCommentaires] = useState(null);
  const [feuille, setFeuille] = useState(null); // 'statut' | 'crm360' | 'vendeur'
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');

  const chargerCommentaires = useCallback(() => {
    leadsWebApi.getCommentaires(lead.id).then((r) => setCommentaires(r.data.commentaires || [])).catch(() => setCommentaires([]));
  }, [lead.id]);
  useEffect(() => { chargerCommentaires(); }, [chargerCommentaires]);

  const fermerFeuille = () => { setFeuille(null); setErreur(''); };

  const changerStatut = async (code) => {
    if (code === lead.statut) { fermerFeuille(); return; }
    if (code === 'crm360') { setFeuille('crm360'); setErreur(''); return; }
    const choisi = options.find((o) => o.code === code);
    setOccupe(true); setErreur('');
    try {
      if (choisi?.archive) {
        await leadsWebApi.update(lead.id, { statut: 'archive' });
        await leadsWebApi.addCommentaire(lead.id, { commentaire: `Lead marqué "${choisi.nom}" — archivé`, type: 'archive' });
        fermerFeuille();
        afficher(`${choisi.nom} · lead archivé`);
        onRetire(lead.id);
        return;
      }
      await leadsWebApi.update(lead.id, { statut: code });
      await leadsWebApi.addCommentaire(lead.id, { commentaire: `Changement de statut → ${choisi?.nom || code}`, type: 'changement_statut' });
      onMaj(lead.id, { statut: code });
      chargerCommentaires();
      fermerFeuille();
      afficher(`Statut : ${choisi?.nom || code}`);
    } catch (e) {
      setErreur(messageErreur(e, 'Le statut n’a pas pu être changé.'));
    } finally {
      setOccupe(false);
    }
  };

  const assigner = async (vendeurId) => {
    setOccupe(true); setErreur('');
    try {
      const r = await leadsWebApi.update(lead.id, { vendeur_id: vendeurId || null });
      const v = vendeurs.find((x) => String(x.id) === String(vendeurId));
      onMaj(lead.id, { ...(r?.data?.lead || {}), vendeur_id: vendeurId || null, vendeur_nom: v?.nom || null, vendeur_prenom: v?.prenom || null });
      fermerFeuille();
      afficher(vendeurId ? `Lead affecté à ${nomVendeur(v)}` : 'Lead désaffecté');
    } catch (e) {
      setErreur(messageErreur(e, 'L’affectation n’a pas pu être enregistrée.'));
    } finally {
      setOccupe(false);
    }
  };

  const aMoi = String(lead.vendeur_id) === String(user?.id);
  const prenom = lead.firstname || '';
  const mailto = lead.email
    ? `mailto:${lead.email}?subject=${encodeURIComponent(`Votre demande${lead.brand ? ` - ${lead.brand}` : ''}`)}&body=${encodeURIComponent(`Bonjour ${prenom},\n\n`)}`
    : null;

  return (
    <Fiche
      titre={`Lead #${lead.id}`}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={<button type="button" className="mb-bouton mb-bouton--principal" onClick={() => setFeuille('statut')}>Changer le statut</button>}
    >
      <div className="mb-entete">
        <div>
          <div className="mb-entete-titre">{nomLead(lead)}</div>
          <div className="mb-entete-sous">Reçu {depuis(lead.date_reception)} · {libelleSource(lead.source)}</div>
        </div>
        <ActionsContact tel={lead.phone} email={lead.email || ''} mailto={mailto} />
        {(lead.phone || lead.email) && (
          <p className="mb-aide">{[lead.phone, lead.email].filter(Boolean).join(' · ')}</p>
        )}
      </div>

      <button type="button" className="mb-statut-bouton" style={{ '--mb-c': statut.couleur }} onClick={() => setFeuille('statut')}>
        <span className="mb-statut-pastille" />
        <b style={{ color: texteTheme(statut.couleur) }}>{statut.nom}</b>
        <small>Vendeur : {lead.vendeur_id ? (aMoi ? 'vous' : [lead.vendeur_prenom, lead.vendeur_nom].filter(Boolean).join(' ')) : 'non assigné'}</small>
        <span className="mb-statut-changer">Changer</span>
      </button>

      {lead.message && (
        <div className="mb-citation">{lead.message}<small>Message du client</small></div>
      )}

      <Bloc titre="Véhicule" resume={vehiculeLead(lead) || '—'}>
        <Kv lignes={[
          ['Type', lead.type_vehicule],
          ['Marque / modèle', vehiculeLead(lead)],
          lead.immatriculation && ['Immatriculation', lead.immatriculation],
          lead.serial_number && ['N° de série', lead.serial_number],
          ['Budget', lead.budget_max ? `${lead.budget_min ? `${Number(lead.budget_min).toLocaleString('fr-FR')} – ` : ''}${Number(lead.budget_max).toLocaleString('fr-FR')} €` : ''],
          lead.financement && ['Financement', lead.financement],
        ]} />
      </Bloc>

      <Bloc titre="Suivi" resume={lead.vendeur_id ? (aMoi ? 'à moi' : 'affecté') : 'à prendre'}>
        <Kv lignes={[
          ['Vendeur', lead.vendeur_id ? [lead.vendeur_prenom, lead.vendeur_nom].filter(Boolean).join(' ') : 'Non assigné'],
          ['Service', lead.service_concession],
          lead.concession_nom && ['Concession', lead.concession_nom],
          lead.subject && ['Sujet', lead.subject],
          lead.commande && ['N° commande', lead.commande],
        ]} />
        <div className="mb-rangee">
          {!aMoi && <button type="button" className="mb-mini mb-mini--accent" disabled={occupe} onClick={() => assigner(user.id)}>Me l’attribuer</button>}
          <button type="button" className="mb-mini" onClick={() => setFeuille('vendeur')}>Changer de vendeur</button>
        </div>
      </Bloc>

      <Bloc titre="Commentaires" compte={commentaires ? commentaires.length : null}
        resume={commentaires && commentaires[0] ? quand(commentaires[0].created_at) : ''} ouvert>
        {commentaires === null && <p className="mb-aide">Chargement…</p>}
        {commentaires && commentaires.length === 0 && <p className="mb-aide">Aucun commentaire.</p>}
        {(commentaires || []).map((c) => (
          <div key={c.id} className="mb-commentaire">
            {c.commentaire}
            <small>{[c.user_prenom, c.user_nom].filter(Boolean).join(' ') || c.username || '—'} · {quand(c.created_at)}</small>
          </div>
        ))}
        <SaisieCommentaire id="mb-lead-com" placeholder="Ajouter un commentaire de suivi"
          onEnvoyer={async (t) => { await leadsWebApi.addCommentaire(lead.id, { commentaire: t, type: 'note' }); chargerCommentaires(); onMaj(lead.id, { nb_commentaires: (lead.nb_commentaires || 0) + 1 }); }} />
      </Bloc>

      <p className="mb-aide mb-aide--centre">Transformer en demande client, historique des modifications : version ordinateur.</p>
      <LienOrdinateur onClick={onVersionOrdinateur} />

      {feuille === 'statut' && (
        <Feuille titre="Statut du lead" onFermer={fermerFeuille}>
          <p className="mb-aide">Service {lead.service_concession || 'VN'}. Un statut marqué « archive » sort le lead de la file.</p>
          <div className="mb-options">
            {options.map((o) => (
              <button key={o.code} type="button" disabled={occupe}
                className={`mb-option${o.code === lead.statut ? ' is-actif' : ''}`}
                style={{ '--mb-c': o.couleur }} onClick={() => changerStatut(o.code)}>
                <span className="mb-option-pastille" />
                {o.nom}
                {(o.archive || o.code === 'crm360') && o.code !== lead.statut && <small>archive</small>}
              </button>
            ))}
          </div>
          {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
        </Feuille>
      )}

      {feuille === 'crm360' && (
        <FeuilleCrm360 lead={lead} onFermer={fermerFeuille}
          onFait={() => { fermerFeuille(); afficher('Lead transféré vers CRM360 et archivé'); onRetire(lead.id); }} />
      )}

      {feuille === 'vendeur' && (
        <FeuilleVendeur vendeurs={vendeurs} actuel={lead.vendeur_id} occupe={occupe} erreur={erreur}
          onChoisir={assigner} onFermer={fermerFeuille} />
      )}
    </Fiche>
  );
}

function FeuilleCrm360({ lead, onFermer, onFait }) {
  const [numero, setNumero] = useState('');
  const [compteRendu, setCompteRendu] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const manque = Math.max(0, CR_CRM360_MIN - compteRendu.trim().length);
  const pret = numero.trim() && manque === 0;
  const valider = async () => {
    if (!pret || envoi) return;
    setEnvoi(true); setErreur('');
    try {
      await leadsWebApi.update(lead.id, { statut: 'archive', crm360_lead_id: numero.trim(), crm360_compte_rendu: compteRendu.trim() });
      onFait();
    } catch (e) {
      setErreur(messageErreur(e, 'Le transfert n’a pas pu être enregistré.'));
      setEnvoi(false);
    }
  };
  return (
    <Feuille titre="Transférer vers CRM360" onFermer={onFermer} fermer="Annuler">
      <div className="mb-champ">
        <label className="mb-etiquette" htmlFor="mb-crm360-n">N° de lead CRM360</label>
        <input id="mb-crm360-n" inputMode="numeric" value={numero} onChange={(e) => setNumero(e.target.value)} />
      </div>
      <div className="mb-champ">
        <label className="mb-etiquette" htmlFor="mb-crm360-cr">Ce qui a été fait avec le client</label>
        <textarea id="mb-crm360-cr" className="mb-zone" value={compteRendu} onChange={(e) => setCompteRendu(e.target.value)}
          placeholder="Ex. : rappelé, essai fixé samedi 9 h, offre envoyée." />
        <p className={`mb-aide${manque ? '' : ' mb-aide--ok'}`}>{manque ? `Encore ${manque} caractère${manque > 1 ? 's' : ''}` : 'Compte rendu complet'}</p>
      </div>
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      <div className="mb-feuille-pied">
        <button type="button" className="mb-bouton" onClick={onFermer}>Annuler</button>
        <button type="button" className="mb-bouton mb-bouton--principal" disabled={!pret || envoi} onClick={valider}>
          {envoi ? 'Envoi…' : 'Transférer et archiver'}
        </button>
      </div>
    </Feuille>
  );
}

function FeuilleVendeur({ vendeurs, actuel, occupe, erreur, onChoisir, onFermer }) {
  const [q, setQ] = useState('');
  const qn = normaliser(q);
  const liste = vendeurs.filter((v) => !qn || normaliser(nomVendeur(v)).includes(qn));
  return (
    <Feuille titre="Affecter à un vendeur" onFermer={onFermer}>
      <Recherche id="mb-vendeur-q" valeur={q} onChange={setQ} placeholder="Nom du vendeur" />
      <div className="mb-options">
        <button type="button" className={`mb-option${!actuel ? ' is-actif' : ''}`} disabled={occupe} onClick={() => onChoisir(null)}>Non assigné</button>
        {liste.map((v) => (
          <button key={v.id} type="button" disabled={occupe}
            className={`mb-option${String(v.id) === String(actuel) ? ' is-actif' : ''}`} onClick={() => onChoisir(v.id)}>
            {nomVendeur(v)}{v.concession_nom && <small>{v.concession_nom}</small>}
          </button>
        ))}
      </div>
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
    </Feuille>
  );
}
